import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { ListOrdered, Plus, Save, Trash2, ChevronUp, ChevronDown, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LISTINO_ICONS } from "./Fatture";
import { toast } from "sonner";

const ICON_KEYS = Object.keys(LISTINO_ICONS);
const fmt = (n) => Number(n || 0).toLocaleString("it-IT");

export default function Listino() {
  const { user } = useAuth();
  const isOwner = user?.role === "Proprietario";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, label, price, icon}
  const [newItem, setNewItem] = useState({ label: "", price: "", icon: "wrench" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/listino");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento listino");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addItem = async () => {
    const price = parseFloat(newItem.price);
    if (!newItem.label.trim()) { toast.error("Inserisci il nome della voce"); return; }
    if (Number.isNaN(price)) { toast.error("Inserisci un prezzo valido"); return; }
    setBusy(true);
    try {
      await api.post("/listino", { label: newItem.label.trim(), price, icon: newItem.icon });
      setNewItem({ label: "", price: "", icon: "wrench" });
      toast.success("Voce aggiunta al listino");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const price = parseFloat(editing.price);
    if (!editing.label.trim()) { toast.error("Inserisci il nome della voce"); return; }
    if (Number.isNaN(price)) { toast.error("Inserisci un prezzo valido"); return; }
    setBusy(true);
    try {
      await api.patch(`/listino/${editing.id}`, { label: editing.label.trim(), price, icon: editing.icon });
      setEditing(null);
      toast.success("Voce aggiornata");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Eliminare la voce "${item.label}" dal listino?`)) return;
    try {
      await api.delete(`/listino/${item.id}`);
      toast.success("Voce eliminata");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    try {
      await api.post("/listino/reorder", { ids: next.map((i) => i.id) });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  if (!isOwner) {
    return (
      <div className="w-full">
        <PageHeader icon={ListOrdered} title="Listino" subtitle="Gestione voci fattura." />
        <div className="ot-panel p-6 text-slate-400 text-sm" data-testid="listino-no-access">
          Solo il Proprietario può gestire il listino prezzi.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        icon={ListOrdered}
        title="Listino"
        subtitle="Aggiungi, modifica e riordina le voci usate nelle fatture."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ot-panel-elev p-6 ot-fade-up">
          <h2 className="ot-heading text-2xl font-bold text-slate-100 mb-4">Voci Attuali</h2>
          {loading ? (
            <div className="text-slate-500 text-sm">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="text-slate-500 text-sm">Nessuna voce nel listino.</div>
          ) : (
            <div className="space-y-2" data-testid="listino-items">
              {items.map((i, idx) => {
                const Icon = LISTINO_ICONS[i.icon] || LISTINO_ICONS.wrench;
                const isEdit = editing?.id === i.id;
                return (
                  <div key={i.id} className="ot-panel p-3" data-testid={`listino-row-${i.id}`}>
                    {isEdit ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="ot-label block mb-1">NOME VOCE</label>
                            <input
                              data-testid="edit-item-label"
                              className="ot-input"
                              value={editing.label}
                              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="ot-label block mb-1">PREZZO ($)</label>
                            <input
                              data-testid="edit-item-price"
                              type="number"
                              className="ot-input ot-mono"
                              value={editing.price}
                              onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {ICON_KEYS.map((k) => {
                            const K = LISTINO_ICONS[k];
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => setEditing({ ...editing, icon: k })}
                                data-testid={`edit-icon-${k}`}
                                className={`p-2 rounded border transition-colors ${editing.icon === k ? "border-blue-400 bg-blue-400/10" : "border-white/10 hover:border-blue-400/40"}`}
                              >
                                <K className="w-4 h-4 text-blue-400" />
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <button data-testid="save-item-btn" disabled={busy} onClick={saveEdit} className="ot-btn-primary flex items-center gap-2">
                            <Save className="w-4 h-4" /> Salva
                          </button>
                          <button data-testid="cancel-edit-btn" onClick={() => setEditing(null)} className="ot-btn-ghost flex items-center gap-2">
                            <X className="w-4 h-4" /> Annulla
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className="w-4 h-4 text-blue-400/70 shrink-0" />
                        <span className="text-slate-200 text-sm flex-1 min-w-[40%]">{i.label}</span>
                        <span className="ot-mono font-bold text-blue-400">${fmt(i.price)}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => move(idx, -1)} data-testid={`up-${i.id}`} className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-white/5 transition-colors">
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button onClick={() => move(idx, 1)} data-testid={`down-${i.id}`} className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-white/5 transition-colors">
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditing({ id: i.id, label: i.label, price: String(i.price), icon: i.icon || "wrench" })}
                            data-testid={`edit-${i.id}`}
                            className="px-2 py-1 rounded text-[0.65rem] ot-heading tracking-widest text-blue-400 border border-blue-400/30 hover:bg-blue-400/10 transition-colors"
                          >
                            MODIFICA
                          </button>
                          <button onClick={() => removeItem(i)} data-testid={`delete-${i.id}`} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ot-panel-elev p-6 ot-fade-up" style={{ animationDelay: "80ms" }}>
          <h2 className="ot-heading text-2xl font-bold text-slate-100 mb-4">Nuova Voce</h2>
          <div className="space-y-3">
            <div>
              <label className="ot-label block mb-1">NOME VOCE</label>
              <input
                data-testid="new-item-label"
                className="ot-input"
                placeholder="Es. Verniciatura completa"
                value={newItem.label}
                onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
              />
            </div>
            <div>
              <label className="ot-label block mb-1">PREZZO ($)</label>
              <input
                data-testid="new-item-price"
                type="number"
                className="ot-input ot-mono"
                placeholder="0"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
              />
            </div>
            <div>
              <label className="ot-label block mb-1">ICONA</label>
              <div className="flex flex-wrap gap-2">
                {ICON_KEYS.map((k) => {
                  const K = LISTINO_ICONS[k];
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setNewItem({ ...newItem, icon: k })}
                      data-testid={`new-icon-${k}`}
                      className={`p-2 rounded border transition-colors ${newItem.icon === k ? "border-blue-400 bg-blue-400/10" : "border-white/10 hover:border-blue-400/40"}`}
                    >
                      <K className="w-4 h-4 text-blue-400" />
                    </button>
                  );
                })}
              </div>
            </div>
            <button data-testid="add-item-btn" disabled={busy} onClick={addItem} className="ot-btn-primary w-full flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Aggiungi al Listino
            </button>
            <p className="text-slate-500 text-xs leading-relaxed">
              Le modifiche si applicano subito nella pagina Fatture per tutti i dipendenti.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
